import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NFForm } from './NFForm';
import type { NFOut, PedidoOut } from '../types/api';

interface NFModalProps {
  pedidos: PedidoOut[];
  existingNF?: NFOut | null;
  suggestedPedidoId?: number | null;
  onClose: () => void;
  onSaved?: (nf: NFOut) => void;
  onDeleted?: (id: number) => void;
}

/**
 * Overlay leve para o fluxo de NF avulsa / bonificação (botão "Anexar nova NF" do topo
 * e edição de bonificações). Todo o formulário vive em <NFForm />, reutilizado também
 * pela aba "Importar/Editar" do PedidoConferenciaModal.
 */
export function NFModal({
  pedidos,
  existingNF,
  suggestedPedidoId,
  onClose,
  onSaved,
  onDeleted,
}: NFModalProps) {
  const isEditing = Boolean(existingNF);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-end md:place-items-center p-2 md:p-4 pointer-events-none">
        <div className="card glow pointer-events-auto w-full max-w-5xl overflow-y-auto rounded-2xl p-4 md:p-6" style={{ maxHeight: '96vh' }}>
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-extrabold text-white">📑 {isEditing ? 'Editar' : 'Importar'} Nota Fiscal</h2>
              <p className="mt-1 text-xs text-mbc-mute">
                Anexe o PDF da NF de produto, revise os dados e associe ao pedido aprovado para concluir a conferência.
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-3xl leading-none text-mbc-mute hover:text-white">×</button>
          </div>

          <NFForm
            pedidos={pedidos}
            existingNF={existingNF}
            suggestedPedidoId={suggestedPedidoId}
            onSaved={nf => { onSaved?.(nf); onClose(); }}
            onDeleted={id => { onDeleted?.(id); onClose(); }}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
